import { AxiosResponse } from 'axios';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { useAccountState } from 'contexts/account/account-context';
import { ErrorHandlerContextActionTypes, useErrorHandlerContextDispatch } from 'contexts/error-handler/ErrorHandlerContext';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';

import { usePost } from 'hooks/use-post';
import { runScenario } from 'views/bluviu-nutrition/features/scenarios/api/bluviu-nutrition';

import LoadingSpinner from 'components/loading-spinner';

import Breadcrumbs from 'components/breadcrumbs';
import Error from 'components/error';

import BluViuNutritionTabsHeader from 'views/bluviu-nutrition/components/bluviu-nutrition-tabs-header';
import RunScenario from 'views/bluviu-nutrition/features/scenarios/components/edit-scenario/run-scenario';
import ScenarioActions from 'views/bluviu-nutrition/features/scenarios/components/edit-scenario/scenario-actions';
import ScenarioProgress from 'views/bluviu-nutrition/features/scenarios/components/edit-scenario/scenario-progress';
import SetupSite from 'views/bluviu-nutrition/features/scenarios/components/edit-scenario/setup-site';
import { ScenarioProvider, useScenarioState } from '../context/scenario-context';
import { ScenariosParams } from '../types/scenarios-params.type';
import FeedPrograms from './edit-scenario/feed-programs';
import Paper from 'components/paper';
import { Box, Typography } from '@mui/material';

type Props = {};

const PAGE_NAME_ID = 'bluViuNutrition.setUp';
const PAGE_NAME = 'bluViuNutrition.listScenarios';

const EditScenario: React.FC<Props> = () => {
  const intl = useIntl();
  const params = useParams<ScenariosParams>();
  const { account } = useAccountState();
  const snackbarDispatch = useSnackbarDispatch();
  const errorHandlerDispatch = useErrorHandlerContextDispatch();
  const queryClient = useQueryClient();
  const {
    account: { company },
  } = useAccountState();
  const {
    scenarioQuery: { data: scenario, error: scenarioError, isError: isScenarioError, isLoading: isScenarioLoading },
  } = useScenarioState();

  // POST DATA
  const submitScenario = usePost<AxiosResponse, { globalCompanyId: string; scenarioId: string }>(
    (request) => runScenario(request.globalCompanyId, request.scenarioId),
    (response) => {
      console.log(response);
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      queryClient.invalidateQueries({
        queryKey: ['getScenario', account.company!.companyId, params.scenarioId]
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
      errorHandlerDispatch({
        type: ErrorHandlerContextActionTypes.ADD_ERRORS,
        payload: {
          errors: [
            {
              code: error.response?.status.toString(),
              message: error.response?.data as string,
            },
          ],
        },
      });
    }
  );

  // HANDLERS

  if (isScenarioLoading) return <LoadingSpinner />;
  if (isScenarioError) return <Error error={scenarioError} />;

  return (
    !!scenario && (
      <>
        <Typography variant="caption" color="white">
          <FormattedMessage id="features.bluViuNutrition" />
        </Typography>
        <Typography variant="h1" color="white">
          <FormattedMessage id="bluViuNutrition.title" />
        </Typography>

        <BluViuNutritionTabsHeader
          intro={
            <Typography color="white">
              <FormattedMessage id={`general.confidential`} />
            </Typography>
          }
          utils={
            <Breadcrumbs
              crumbs={
                !!params.globalSiteId
                  ? [
                      {
                        text: company?.addressAccess.find((s) => s.id === params.globalSiteId)?.name || '', // backup from location state
                        url: `${params.globalSiteId}`,
                      },
                      {
                        text: scenario.name || '',
                        url: `${params.globalSiteId}/${params.scenarioId}`,
                      },
                    ]
                  : undefined
              }
            />
          }
        />

        <Box display={'flex'} flexDirection={'column'} gap={4}>
          <ScenarioProgress />
          <Paper transparent>
            <Box display={'flex'} flexDirection={'column'} gap={4}>
              <ScenarioActions />
              <SetupSite isLoading={submitScenario.isPending} />
              <FeedPrograms isLoading={submitScenario.isPending} />
              <RunScenario />
            </Box>
          </Paper>
        </Box>
      </>
    )
  );
};

const ScenarioContainer: React.FC<{}> = () => {
  return (
    <ScenarioProvider>
      <EditScenario />
    </ScenarioProvider>
  );
};

export default React.memo(ScenarioContainer);
